import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { PermissionGate } from "@/components/common/PermissionGate";
import {
  MoreHorizontal,
  Eye,
  CalendarClock,
  Trash2,
  LifeBuoy,
  MailWarning,
  RefreshCcwDot,
  Router,
  HardDriveDownload,
  RotateCcw,
  Archive,
  BookOpen,
} from "lucide-react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useResourcePowerActions } from "@/hooks/useResourcePowerActions";
import {
  useFinalizeResourceDeletion,
  useRestoreDeletedResource,
  useSendResourceExpiryReminder,
} from "@/hooks/useResourceQueries";
import { canFinalizeDeletion, canRestoreDeletion } from "@/lib/providerVm";
import { usePermissions } from "@/hooks/usePermissions";
import type { ProvisioningRetryCheck, Resource } from "@/types/admin";

interface ResourceActionsDropdownProps {
  resource: Resource;

  /** Opens the recovery page for this resource. VM rows only. */
  onUpdateByUuid: () => void;

  /** Opens the change-expiry dialog for this resource. */
  onChangeExpiry: () => void;

  /** Opens the change-status dialog for this resource. */
  onChangeStatus: () => void;

  /**
   * Opens the change-IP dialog for this resource. VM rows only. The dialog is where the operator
   * picks whether the change lands on WiCloud's record alone or on the machine itself.
   */
  onChangeIp: () => void;

  /** Opens the delete confirmation for this resource. */
  onDelete: () => void;

  /** True while this row's delete mutation is in flight. */
  isDeleting?: boolean;

  /**
   * Opens the in-place reinstall dialog for a LIVE VM — one request, no wipe step (design doc §5a).
   * Optional because a call site that shows deleted rows has nothing live to reinstall.
   */
  onReinstallLive?: () => void;

  /**
   * Whether this row is a deleted resource (`deletedAt` set). A deleted Decommissioning row is a VM
   * deleted at the provider and not finalized yet; the very same status on a live row means a
   * reinstall is already running (the backend clears `DeletedAt` the moment one starts).
   */
  isDeleted?: boolean;

  /**
   * Server-decided (2026-09-28): whether "Retry provisioning" may be offered for this row —
   * batched by the table for every Error VM on the page. Undefined until that check lands, same
   * as "no item yet" on the detail page.
   */
  retryCheck?: ProvisioningRetryCheck;

  /** Opens the retry-provisioning confirmation for this row. Undefined off a non-VM/non-Error row. */
  onRetryProvisioning?: () => void;
}

/**
 * Row actions for the resource table. Mirrors `OrderActionsDropdown`: every action lives behind
 * one trigger so the sticky Actions column stays narrow no matter how many actions are added.
 *
 * Every write action is SuperAdmin-only, matching [AdminRole(SuperAdmin)] on the admin delete, expiry
 * and power endpoints — a SupportAdmin still gets "View details".
 */
export function ResourceActionsDropdown({
  resource,
  onUpdateByUuid,
  onChangeExpiry,
  onChangeStatus,
  onChangeIp,
  onDelete,
  isDeleting,
  onReinstallLive,
  isDeleted = false,
  retryCheck,
  onRetryProvisioning,
}: ResourceActionsDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const {
    actions: powerActions,
    confirmDialog: powerConfirmDialog,
    isPending: isRunningPowerAction,
    blockedReason: powerBlockedReason,
  } = useResourcePowerActions(resource);
  const sendReminder = useSendResourceExpiryReminder();
  const finalizeDeletion = useFinalizeResourceDeletion();
  const [confirmFinalizeOpen, setConfirmFinalizeOpen] = useState(false);
  const restoreDeletion = useRestoreDeletedResource();
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const { isSuperAdmin } = usePermissions();

  // Shown only when the API would accept it: a deleted VM whose kept window has closed. Read when the
  // menu renders, so a row that crosses the line while the page sits open gains the item on the next
  // open. Inside the SuperAdmin gate below, like every other write.
  const showFinalizeDeletion = canFinalizeDeletion(resource);
  // Its mirror: a deleted VM still inside its window can be brought back.
  const showRestoreDeletion = canRestoreDeletion(resource);

  /**
   * The in-place reinstall, offered on a LIVE VM. Excluded from the deleted views (the pending-purge
   * and Deleted filters), which show a row that is not live at all.
   *
   * Shown for the rest, even when the VM is not Stopped: the dialog is where "shut it down first",
   * "no machine to rebuild" and "the term has ended" are explained, and an item silently missing
   * from a menu explains nothing. Except Error: it cannot be stopped, so the dialog could only say
   * "shut it down first"; a failed VM is retried or recovered, never reinstalled.
   */
  const canReinstallLive =
    !isDeleted &&
    resource.type === "VirtualMachine" &&
    onReinstallLive !== undefined &&
    resource.status !== "Terminating" &&
    resource.status !== "Terminated" &&
    resource.status !== "Provisioning" &&
    resource.status !== "Decommissioning" &&
    resource.status !== "Error";

  /**
   * The delete endpoint answers 409 for these. `Provisioning` is blocked because deleting
   * mid-provision races ProvisionResourceJob and can orphan a VM on the hypervisor.
   * `Decommissioning` is blocked because the row is either already deleted (isDeleted) or
   * mid-reinstall already (RESOURCE_REINSTALLING, the live view).
   */
  const isNotDeletable =
    resource.status === "Terminating" ||
    resource.status === "Terminated" ||
    resource.status === "Provisioning" ||
    resource.status === "Decommissioning";

  // Expiry only means something for a VM — the expiry scan jobs act on virtual machines alone —
  // and the endpoint rejects a resource that is on its way out.
  const canEditExpiry =
    resource.type === "VirtualMachine" &&
    resource.status !== "Terminating" &&
    resource.status !== "Terminated";

  const deleteBlockedReason =
    resource.status === "Provisioning"
      ? "Still provisioning — wait for Running or Error"
      : resource.status === "Decommissioning"
        ? isDeleted
          ? "Already deleted"
          : "Being reinstalled"
        : "Already deleted";

  /**
   * The backend now refuses both "Change IP address" (machine path — `RESOURCE_REINSTALLING`) and
   * "Update resource by UUID" while a reinstall is running. Recovery keeps its own item hidden
   * on `Terminated`/`Terminating` as before; `Decommissioning` stays visible but disabled with a
   * reason, since it is transient rather than terminal. The same status in the deleted view is a
   * row still inside its pending-purge window, not a reinstall — `deleteBlockedReason` above makes
   * the same split.
   */
  const updateBlockedReason =
    resource.status === "Decommissioning"
      ? isDeleted
        ? "Already deleted"
        : "Being reinstalled"
      : undefined;

  // While a power action or the reminder is in flight, this row's other writes are held — an
  // operator must not queue a status change or a delete on a VM halfway through a restart — and the
  // trigger shows the spinner. The menu itself closes on select: a Radix menu is modal, so holding
  // it open for a provider round trip locked the whole console with nothing on screen saying why.
  const isMenuBusy =
    isRunningPowerAction ||
    sendReminder.isPending ||
    finalizeDeletion.isPending ||
    restoreDeletion.isPending;

  const runAndClose = (action: () => void) => {
    action();
    setOpen(false);
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-busy={isMenuBusy}
          >
            <span className="sr-only">Open menu</span>
            {isMenuBusy ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        {/* Three tiers, and the split is what each one acts on rather than how dangerous it feels:
            "Inspect" only opens a view, "Virtual machine" reaches the hypervisor, and "WiCloud
            record" changes nothing but WiCloud's own data. Delete is the only item that does both,
            which is why it sits alone at the bottom. The detail page's overflow menu is grouped the
            same way, on purpose — the same VM, the same actions, the same order. */}
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Inspect
          </DropdownMenuLabel>
          {/* A deleted row has no detail page: it is hidden from the lookup behind it. */}
          <DropdownMenuItem
            disabled={isDeleted}
            onClick={() =>
              // Routes are mounted under /admin and there is no router basename.
              runAndClose(() => navigate(`/admin/resources/${resource.id}`))
            }
          >
            <Eye className="mr-2 h-4 w-4" />
            View details
          </DropdownMenuItem>
          {/* A real link, so the guide opens in its own tab beside the list. It starts at "which
              tool do I use", the question an operator opening this menu on a broken row has. */}
          <DropdownMenuItem asChild className="cursor-pointer">
            <a
              href="/docs/vm-actions.html#which-tool"
              target="_blank"
              rel="noreferrer"
            >
              <BookOpen className="mr-2 h-4 w-4" />
              How these actions work
            </a>
          </DropdownMenuItem>

          {/* Server-decided (2026-09-28): `retryCheck` is the batch check the table ran for every
              Error VM row on the page, open to every admin role — so this is shown whenever it
              says allowed, not gated behind PermissionGate like the hypervisor actions below. Only
              a SuperAdmin may actually press it; the POST stays SuperAdmin-only on the server. */}
          {retryCheck?.allowed === true && onRetryProvisioning && (
            <DropdownMenuItem
              disabled={isMenuBusy || !isSuperAdmin()}
              title={
                !isSuperAdmin()
                  ? "Only a SuperAdmin can retry provisioning"
                  : undefined
              }
              onClick={() => runAndClose(onRetryProvisioning)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Retry provisioning…
            </DropdownMenuItem>
          )}

          {/* Its own group: this is the only action here that leaves WiCloud and reaches the
              customer, while touching neither the hypervisor nor the record. Offered to
              SupportAdmin as well — they are who takes the call when a customer says the reminder
              never arrived. Shown on any VM rather than only inside the reminder window, because
              the endpoint refuses with a sentence naming the reason (no term, too far out, no VM
              UUID on record) and that reads better than an item silently missing. */}
          {resource.type === "VirtualMachine" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                Customer
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={isMenuBusy}
                // Closes on select like every other item: the trigger spins until the request
                // answers, and the toast reports the outcome.
                onClick={() =>
                  runAndClose(() => sendReminder.mutate(resource.id))
                }
              >
                {sendReminder.isPending ? (
                  <Spinner className="mr-2" />
                ) : (
                  <MailWarning className="mr-2 h-4 w-4" />
                )}
                Send expiry reminder
              </DropdownMenuItem>
            </>
          )}

          <PermissionGate roles="SuperAdmin">
            {/* All seven, always, for any status these two grouped statuses don't block — the
                only items here that reach the hypervisor. The admin endpoint refuses
                Terminating/Decommissioning with INVALID_STATE (both are already their own round
                trip to the provider), so those two disable the group with a reason instead of
                leaving it to come back as a toast. Anything else the VM cannot take is refused by
                the provider and reported the same way. */}
            {powerActions.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  Virtual machine
                </DropdownMenuLabel>
              </>
            )}
            {powerActions.map(
              ({ action, label, Icon, destructive, isRunning, run }) =>
                destructive ? (
                  // Opens a confirmation instead of firing, so the menu can close right away —
                  // the dialog is where this action reports back.
                  <DropdownMenuItem
                    key={action}
                    className="text-destructive-ink focus:text-destructive-ink"
                    disabled={isMenuBusy || powerBlockedReason !== null}
                    title={powerBlockedReason ?? undefined}
                    onClick={() => runAndClose(run)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {label}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    key={action}
                    disabled={isMenuBusy || powerBlockedReason !== null}
                    title={powerBlockedReason ?? undefined}
                    // Closes on select: the trigger spins and this row's other writes stay held
                    // until the request settles, and the toast reports the outcome.
                    onClick={() => runAndClose(() => run())}
                  >
                    {isRunning ? (
                      <Spinner className="mr-2" />
                    ) : (
                      <Icon className="mr-2 h-4 w-4" />
                    )}
                    {label}
                  </DropdownMenuItem>
                ),
            )}
            {/* One entry point for both layers; the dialog is where the operator chooses whether
                the change reaches the machine or only WiCloud's record. Grouped with the actions
                that touch the hypervisor rather than under "WiCloud record", because the group has
                to describe the worst this item can do — and a mis-click that re-addresses a
                customer's VM is exactly that. */}
            {resource.type === "VirtualMachine" && (
              <DropdownMenuItem
                disabled={isMenuBusy}
                onClick={() => runAndClose(onChangeIp)}
              >
                <Router className="mr-2 h-4 w-4" />
                Change IP address
              </DropdownMenuItem>
            )}

            {/* The in-place reinstall: one request, kept next to Change IP address because it
                reaches the hypervisor the same way and costs more — the disk is erased. */}
            {canReinstallLive && onReinstallLive && (
              <DropdownMenuItem
                className="text-destructive-ink focus:text-destructive-ink"
                disabled={isMenuBusy}
                onClick={() => runAndClose(onReinstallLive)}
              >
                <HardDriveDownload className="mr-2 h-4 w-4" />
                Reinstall…
              </DropdownMenuItem>
            )}
          </PermissionGate>

          {/* Nothing in this group touches the infrastructure — every one of them only corrects
              what WiCloud holds about the resource. Rendered for a non-VM too: it has nothing to
              recover, but its record drifts the same way, and the group label describes "Change
              status" exactly. SuperAdmin only, like every item under it — a SupportAdmin would
              otherwise see a heading over nothing. */}
          <PermissionGate roles="SuperAdmin">
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              WiCloud record
            </DropdownMenuLabel>
          </PermissionGate>

          {resource.type === "VirtualMachine" && (
            <>
              {/* A VM whose build never finished is invisible to the status sync — it only
                    revisits rows already Running or Stopped — so it stays broken for ever while
                    the customer is billed. This is the only way back, and since 2026-10-26 also
                    how a drifted record (vCPU, memory, disk) is corrected: it replaced "Update
                    mismatched configuration". Offered wherever a UUID could exist; the endpoint
                    refuses a deleted resource. */}
              <PermissionGate roles="SuperAdmin">
                {resource.status !== "Terminated" &&
                  resource.status !== "Terminating" && (
                    <DropdownMenuItem
                      disabled={isMenuBusy || updateBlockedReason !== undefined}
                      title={updateBlockedReason}
                      onClick={() => runAndClose(onUpdateByUuid)}
                    >
                      <LifeBuoy className="mr-2 h-4 w-4" />
                      Update resource by UUID
                    </DropdownMenuItem>
                  )}
              </PermissionGate>
            </>
          )}

          {/* Update resource by UUID asks the provider what is really there; this one asks
                nobody and simply writes the status down. It is the last resort of the two, for a
                row the provider can no longer answer for — so it is offered on every status and
                every resource type, and the dialog is where the risky choices are explained. */}
          {/* A deleted row is out of the endpoint's reach (404), so it is shown disabled with the
              reason rather than opening a dialog whose save can only fail. */}
          <PermissionGate roles="SuperAdmin">
            <DropdownMenuItem
              disabled={isMenuBusy || isDeleted}
              title={isDeleted ? "Already deleted" : undefined}
              onClick={() => runAndClose(onChangeStatus)}
            >
              <RefreshCcwDot className="mr-2 h-4 w-4" />
              Change status
            </DropdownMenuItem>
          </PermissionGate>

          <PermissionGate roles="SuperAdmin">
            {canEditExpiry && (
              <DropdownMenuItem
                disabled={isMenuBusy}
                onClick={() => runAndClose(onChangeExpiry)}
              >
                <CalendarClock className="mr-2 h-4 w-4" />
                Change expiry date
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />
            {showRestoreDeletion && (
              <DropdownMenuItem
                disabled={isMenuBusy}
                onClick={() => runAndClose(() => setConfirmRestoreOpen(true))}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Restore VM…
              </DropdownMenuItem>
            )}
            {/* For a deleted VM whose scheduled finalize never ran: nothing scans for those. */}
            {showFinalizeDeletion && (
              <DropdownMenuItem
                className="text-destructive-ink focus:text-destructive-ink"
                disabled={isMenuBusy}
                onClick={() => runAndClose(() => setConfirmFinalizeOpen(true))}
              >
                <Archive className="mr-2 h-4 w-4" />
                Finalize deletion now…
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive-ink focus:text-destructive-ink"
              disabled={isNotDeletable || isDeleting || isMenuBusy}
              title={isNotDeletable ? deleteBlockedReason : undefined}
              onClick={() => runAndClose(onDelete)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete resource
            </DropdownMenuItem>
          </PermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Sibling of the menu, not a child: the confirmation must survive the menu closing. */}
      {powerConfirmDialog}
      <ConfirmDialog
        open={confirmRestoreOpen}
        onOpenChange={setConfirmRestoreOpen}
        title="Restore this VM?"
        description={
          <>
            {`"${resource.name}" is rebuilt on its original uuid with its current OS, IP, CPU, RAM and disk size, and keeps its paid term. Disk data, firewall rules and snapshots do not come back, and the customer gets a new password. It is queued: usually Running within a few minutes; if the infrastructure refuses, it returns to the deleted list; if it is not running after 10 minutes, it goes to Error. Refused when the paid term has ended or another resource took its name. `}
            <GuideLink href="/docs/vm-delete.html#restore" />
          </>
        }
        confirmText="Restore"
        isPending={restoreDeletion.isPending}
        onConfirm={() =>
          restoreDeletion.mutate(resource.id, {
            onSettled: () => setConfirmRestoreOpen(false),
          })
        }
      />
      <ConfirmDialog
        open={confirmFinalizeOpen}
        onOpenChange={setConfirmFinalizeOpen}
        title="Finalize this deletion now?"
        description={
          <>
            {`"${resource.name}" was deleted and its restore window has closed, but its scheduled finalize has not run. This queues it: the VM and its disks and networks become Terminated and are detached. The customer sees a "deleted" entry in their activity log; no email is sent. An open renewal order is not closed. This cannot be undone. `}
            <GuideLink href="/docs/vm-delete.html#finalize" />
          </>
        }
        confirmText="Finalize"
        variant="destructive"
        isPending={finalizeDeletion.isPending}
        onConfirm={() =>
          finalizeDeletion.mutate(resource.id, {
            onSettled: () => setConfirmFinalizeOpen(false),
          })
        }
      />
    </>
  );
}

/** "Full guide" link inside a confirmation's description, opened in its own tab. */
function GuideLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-foreground underline underline-offset-2"
    >
      Full guide
    </a>
  );
}
